import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { BsCheckCircleFill, BsGlobe } from 'react-icons/bs';

const STAGES = [
	{ label: 'Website wird geladen', doneAfterS: 6 },
	{ label: 'Unterseiten werden gelesen', doneAfterS: 16 },
	{ label: 'KI analysiert Angebot & Zielgruppe', doneAfterS: Infinity },
];

/**
 * Progress asymptotically approaches 92% so it keeps moving on slow crawls
 * without ever pretending to be finished.
 * @param {number} elapsedSec
 * @returns {number} percent 0-92
 */
const progressPercent = (elapsedSec) => Math.round(92 * (1 - Math.exp(-elapsedSec / 25)));

const AnalyzingView = ({ elapsedSec }) => {
	// Self-timing fallback for callers that don't track elapsed time themselves
	const hasExternalTimer = elapsedSec !== undefined;
	const [internalElapsed, setInternalElapsed] = useState(0);
	useEffect(() => {
		if (hasExternalTimer) return undefined;
		const startedAt = Date.now();
		const id = setInterval(() => setInternalElapsed(Math.round((Date.now() - startedAt) / 1000)), 1000);
		return () => clearInterval(id);
	}, [hasExternalTimer]);
	const seconds = hasExternalTimer ? elapsedSec : internalElapsed;

	return (
		<div className="text-center py-6 sm:py-8">
			<div className="mx-auto mb-4 h-14 w-14 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center animate-pulse">
				<BsGlobe className="w-7 h-7" />
			</div>
			<h2 className="text-lg font-semibold text-text mb-1">Wir analysieren Ihre Website …</h2>
			<p className="text-sm text-bgBase-800 max-w-sm mx-auto">
				{seconds < 45
					? 'Das dauert meist unter einer Minute.'
					: 'Gründliche Analyse läuft — umfangreiche Websites dauern etwas länger.'}
			</p>

			<div className="mt-6 max-w-xs mx-auto">
				<div className="h-1.5 rounded-full bg-bgBase-400 overflow-hidden">
					<div
						className="h-full bg-primary-500 transition-[width] duration-1000 ease-linear"
						style={{ width: `${progressPercent(seconds)}%` }}
					/>
				</div>
			</div>

			<div className="mt-6 max-w-xs mx-auto space-y-2.5 text-left">
				{STAGES.map((stage, idx) => {
					const isDone = seconds >= stage.doneAfterS;
					const isActive = !isDone && (idx === 0 || seconds >= STAGES[idx - 1].doneAfterS);

					return (
						<div key={stage.label} className="flex items-center gap-2.5">
							{isDone ? (
								<BsCheckCircleFill className="w-4 h-4 text-success-500 flex-shrink-0" />
							) : (
								<span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
									<span
										className={`w-2 h-2 rounded-full ${isActive ? 'bg-primary-500 animate-pulse' : 'bg-bgBase-600'}`}
									/>
								</span>
							)}
							<span
								className={`text-sm ${isDone ? 'text-success-700' : isActive ? 'text-text font-medium' : 'text-bgBase-800'}`}
							>
								{stage.label} …
							</span>
						</div>
					);
				})}
			</div>
		</div>
	);
};

AnalyzingView.propTypes = {
	elapsedSec: PropTypes.number,
};

export default AnalyzingView;
